/**
 * Audit Forms Hook - Survey Readiness & Regulatory Compliance
 *
 * FERP CRITICAL: Regulatory survey preparation and checkpoint tracking
 *
 * Backend: AuditFormRepository (audit_form_api/)
 * Primitive: Audit (extended for survey context)
 *
 * Features:
 * - CMS survey forms (F-tags, K-tags, E-tags)
 * - State survey tools (MN 144G, etc.)
 * - Critical/life-safety checkpoint identification
 * - Top deficiency analysis for proactive preparation
 * - Evidence requirement tracking
 *
 * Domain-Agnostic: "Audit" is the generic primitive.
 * Healthcare context: Survey readiness, regulatory compliance.
 * ERP context: SOX audit forms, internal audit checklists.
 * Legal context: Compliance audit trails.
 */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================
// GraphQL Operations (from queries-audit-forms.graphql)
// ============================================================

const LIST_AUDIT_FORMS = gql`
  query ListAuditForms($program: String, $jurisdiction: String, $limit: Int) {
    auditForms(program: $program, jurisdiction: $jurisdiction, limit: $limit) {
      formId
      name
      description
      program
      jurisdiction
      regulatoryBody
      effectiveDate
      version
      checkpointCount
      criticalCount
      tags
    }
  }
`;

const GET_AUDIT_FORM = gql`
  query GetAuditForm($formId: String!) {
    auditForm(formId: $formId) {
      formId
      name
      description
      program
      jurisdiction
      regulatoryBody
      effectiveDate
      version
      checkpointCount
      criticalCount
      sections {
        sectionId
        name
        description
        checkpointCount
      }
      tags
      sourceDocument
      cfrReferences
    }
  }
`;

const GET_AUDIT_FORM_BY_CFR = gql`
  query GetAuditFormByCfr($cfr: String!) {
    auditFormByCfr(cfr: $cfr) {
      formId
      name
      description
      program
      jurisdiction
      regulatoryBody
      cfrReferences
    }
  }
`;

const GET_AUDIT_CHECKPOINTS = gql`
  query GetAuditCheckpoints($formId: String!, $priority: String) {
    auditCheckpoints(formId: $formId, priority: $priority) {
      checkpointId
      formId
      sectionId
      tagNumber
      description
      guidance
      priority
      isCritical
      isLifeSafety
      regulatoryCitation
      evidenceRequirements
      commonDeficiencies
      correctionTimeframe
    }
  }
`;

const GET_CRITICAL_CHECKPOINTS = gql`
  query GetCriticalCheckpoints($program: String, $jurisdiction: String) {
    criticalCheckpoints(program: $program, jurisdiction: $jurisdiction) {
      checkpointId
      formId
      tagNumber
      description
      isLifeSafety
      regulatoryCitation
      correctionTimeframe
    }
  }
`;

const GET_TOP_DEFICIENCIES = gql`
  query GetTopDeficiencies($program: String, $regulatoryBody: String, $limit: Int) {
    topDeficiencies(program: $program, regulatoryBody: $regulatoryBody, limit: $limit) {
      tagNumber
      description
      frequency
      averageSeverity
      commonRootCauses
      preventionTips
      regulatoryCitation
    }
  }
`;

// ============================================================
// Variable Types
// ============================================================

interface ListAuditFormsVariables {
  program?: string;
  jurisdiction?: string;
  limit?: number;
}

interface GetAuditCheckpointsVariables {
  formId: string;
  priority?: string;
}

interface GetCriticalCheckpointsVariables {
  program?: string;
  jurisdiction?: string;
}

interface GetTopDeficienciesVariables {
  program?: string;
  regulatoryBody?: string;
  limit?: number;
}

// ============================================================
// Types (from codegen, re-exported for convenience)
// ============================================================

export interface AuditForm {
  formId: string;
  name: string;
  description?: string;
  program?: string;
  jurisdiction?: string;
  regulatoryBody?: string;
  effectiveDate?: string;
  version?: string;
  checkpointCount: number;
  criticalCount: number;
  tags?: string[];
  sourceDocument?: string;
  cfrReferences?: string[];
  sections?: AuditFormSection[];
}

export interface AuditFormSection {
  sectionId: string;
  name: string;
  description?: string;
  checkpointCount: number;
}

export interface AuditCheckpoint {
  checkpointId: string;
  formId: string;
  sectionId?: string;
  tagNumber: string;
  description: string;
  guidance?: string;
  priority: string;
  isCritical: boolean;
  isLifeSafety: boolean;
  regulatoryCitation?: string;
  evidenceRequirements?: string[];
  commonDeficiencies?: string[];
  correctionTimeframe?: string;
}

export interface DeficiencyAnalysis {
  tagNumber: string;
  description: string;
  frequency: number;
  averageSeverity: number;
  commonRootCauses?: string[];
  preventionTips?: string[];
  regulatoryCitation?: string;
}

// ============================================================
// Hook: List Audit Forms
// ============================================================

export function useAuditFormsList(variables?: ListAuditFormsVariables) {
  const { data, loading, error, refetch } = useQuery(LIST_AUDIT_FORMS, {
    variables: variables || {},
    fetchPolicy: 'cache-and-network',
  });

  return {
    forms: (data?.auditForms ?? []) as AuditForm[],
    loading,
    error,
    refetch,
  };
}

// ============================================================
// Hook: Get Single Audit Form
// ============================================================

export function useAuditForm(formId: string) {
  const { data, loading, error, refetch } = useQuery(GET_AUDIT_FORM, {
    variables: { formId },
    skip: !formId,
  });

  return {
    form: (data?.auditForm ?? null) as AuditForm | null,
    loading,
    error,
    refetch,
  };
}

// ============================================================
// Hook: Get Audit Form by CFR Reference
// ============================================================

export function useAuditFormByCfr(cfr: string) {
  const { data, loading, error } = useQuery(GET_AUDIT_FORM_BY_CFR, {
    variables: { cfr },
    skip: !cfr,
  });

  return {
    form: (data?.auditFormByCfr ?? null) as AuditForm | null,
    loading,
    error,
  };
}

// ============================================================
// Hook: Get Checkpoints for Audit Form
// ============================================================

export function useAuditCheckpoints(variables: GetAuditCheckpointsVariables) {
  const { data, loading, error, refetch } = useQuery(GET_AUDIT_CHECKPOINTS, {
    variables,
    skip: !variables.formId,
  });

  const checkpoints = (data?.auditCheckpoints ?? []) as AuditCheckpoint[];

  // Computed: group by section
  const groupedBySection = useCallback(() => {
    const groups: Record<string, AuditCheckpoint[]> = {};
    for (const cp of checkpoints) {
      const section = cp.sectionId || 'unsectioned';
      if (!groups[section]) groups[section] = [];
      groups[section].push(cp);
    }
    return groups;
  }, [checkpoints]);

  // Computed: filter critical only
  const criticalOnly = useCallback(() => {
    return checkpoints.filter((cp) => cp.isCritical);
  }, [checkpoints]);

  // Computed: filter life-safety only
  const lifeSafetyOnly = useCallback(() => {
    return checkpoints.filter((cp) => cp.isLifeSafety);
  }, [checkpoints]);

  return {
    checkpoints,
    loading,
    error,
    refetch,
    groupedBySection,
    criticalOnly,
    lifeSafetyOnly,
  };
}

// ============================================================
// Hook: Get Critical Checkpoints (Cross-Form)
// ============================================================

export function useCriticalCheckpoints(variables?: GetCriticalCheckpointsVariables) {
  const { data, loading, error, refetch } = useQuery(GET_CRITICAL_CHECKPOINTS, {
    variables: variables || {},
  });

  const checkpoints = (data?.criticalCheckpoints ?? []) as AuditCheckpoint[];

  // Computed: group by form
  const groupedByForm = useCallback(() => {
    const groups: Record<string, AuditCheckpoint[]> = {};
    for (const cp of checkpoints) {
      if (!groups[cp.formId]) groups[cp.formId] = [];
      groups[cp.formId].push(cp);
    }
    return groups;
  }, [checkpoints]);

  // Computed: life-safety items (highest priority)
  const lifeSafetyItems = useCallback(() => {
    return checkpoints.filter((cp) => cp.isLifeSafety);
  }, [checkpoints]);

  return {
    checkpoints,
    loading,
    error,
    refetch,
    groupedByForm,
    lifeSafetyItems,
    totalCount: checkpoints.length,
    lifeSafetyCount: checkpoints.filter((cp) => cp.isLifeSafety).length,
  };
}

// ============================================================
// Hook: Get Top Deficiencies (Survey Intelligence)
// ============================================================

export function useTopDeficiencies(variables?: GetTopDeficienciesVariables) {
  const { data, loading, error, refetch } = useQuery(GET_TOP_DEFICIENCIES, {
    variables: variables || {},
  });

  const deficiencies = (data?.topDeficiencies ?? []) as DeficiencyAnalysis[];

  // Computed: high severity (>= 3 on 1-4 scale)
  const highSeverity = useCallback(() => {
    return deficiencies.filter((d) => d.averageSeverity >= 3);
  }, [deficiencies]);

  // Computed: most frequent (top 5)
  const mostFrequent = useCallback(() => {
    return [...deficiencies].sort((a, b) => b.frequency - a.frequency).slice(0, 5);
  }, [deficiencies]);

  return {
    deficiencies,
    loading,
    error,
    refetch,
    highSeverity,
    mostFrequent,
  };
}

// ============================================================
// Combined Hook: Survey Readiness Dashboard
// ============================================================

export function useSurveyReadiness(program?: string, jurisdiction?: string) {
  const forms = useAuditFormsList({ program, jurisdiction });
  const critical = useCriticalCheckpoints({ program, jurisdiction });
  const deficiencies = useTopDeficiencies({ program, limit: 10 });

  const loading = forms.loading || critical.loading || deficiencies.loading;
  const error = forms.error || critical.error || deficiencies.error;

  return {
    // Forms available for this program/jurisdiction
    forms: forms.forms,

    // Critical checkpoints to focus on
    criticalCheckpoints: critical.checkpoints,
    lifeSafetyCount: critical.lifeSafetyCount,

    // Top deficiencies to prepare for
    topDeficiencies: deficiencies.mostFrequent(),
    highSeverityDeficiencies: deficiencies.highSeverity(),

    // Loading/error state
    loading,
    error,

    // Refetch all
    refetch: async () => {
      await Promise.all([forms.refetch(), critical.refetch(), deficiencies.refetch()]);
    },
  };
}
